import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, X, Film, Sparkles } from 'lucide-react';

/**
 * VideoReel — auto-scrolling horizontal carousel of student/ceremony video thumbnails.
 * REPLACE WITH REAL VIDEOS — update `videos` array with your YouTube/Vimeo URLs and real thumbnails.
 *
 * For each entry:
 *   - `thumb`  : poster image (1280×720 recommended)
 *   - `videoUrl`: YouTube embed URL (e.g. https://www.youtube.com/embed/VIDEO_ID)
 *                 OR a direct .mp4 link.
 */
const videos = [
  {
    id: 'life-changing-moments',
    title: 'Celebrating the Class of 2024 — Caucasus University Medical Graduates',
    sub: 'Patrick\'s Edu Care · Graduation moments',
    thumb: 'https://i.ytimg.com/vi/RaZhJF-4J_4/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/embed/RaZhJF-4J_4',
    duration: 'Watch'
  },
  {
    id: 'student-story-1',
    title: 'Why Neha chose Caucasus University for higher studies in Georgia',
    sub: 'Patrick\'s Edu Care · Real student review',
    thumb: 'https://i.ytimg.com/vi/i4_QNILislQ/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/embed/i4_QNILislQ',
    duration: 'Watch'
  },
  {
    id: 'student-story-2',
    title: 'Dr. Patrick speaks at the Asianet Study Abroad Expo',
    sub: 'Patrick\'s Edu Care · Expo highlight',
    thumb: 'https://i.ytimg.com/vi/_C1sxHeyhAk/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/embed/_C1sxHeyhAk',
    duration: 'Watch'
  },
  {
    id: 'student-story-3',
    title: 'Devika shares her clinical experience as a 5th-year medical student',
    sub: 'Patrick\'s Edu Care · Real student journey',
    thumb: 'https://i.ytimg.com/vi/zcfx0gmgmI0/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/embed/zcfx0gmgmI0',
    duration: 'Watch'
  },
  {
    id: 'student-story-4',
    title: 'Real student review — studies, exams & campus life at Caucasus University',
    sub: 'Patrick\'s Edu Care · Campus life',
    thumb: 'https://i.ytimg.com/vi/zHmaupHW26o/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/embed/zHmaupHW26o',
    duration: 'Watch'
  },
  {
    id: 'student-story-5',
    title: 'How Megha continued MBBS after the Ukraine war — real student experience',
    sub: 'Patrick\'s Edu Care · Student journey',
    thumb: 'https://i.ytimg.com/vi/PGFmHWsNJdo/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/embed/PGFmHWsNJdo',
    duration: 'Watch'
  }
];

const VideoReel = () => {
  const [activeVideo, setActiveVideo] = useState(null);

  return (
    <section className="py-10 md:py-14 bg-[#F8FAFC] relative overflow-hidden" data-testid="video-reel">
      <div className="absolute top-10 right-0 w-72 h-72 rounded-full bg-[#4DD4AC]/5 blur-3xl pointer-events-none" />
      <div className="container mx-auto px-4 relative">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0, margin: '0px 0px 200px 0px' }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8 max-w-2xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E63946]/10 text-[#E63946] text-xs font-semibold mb-3">
            <Film size={12} /> Moments You'll Love
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-[#1A2B3C] mb-2">
            Send-offs, graduations & <br className="hidden sm:block" />
            <span className="bg-gradient-to-r from-[#E63946] to-[#FF6B6B] bg-clip-text text-transparent">life-changing moments.</span>
          </h2>
          <p className="text-sm md:text-base text-[#4A5B6C]">
            A few minutes of real moments speaks louder than any brochure.
          </p>
        </motion.div>

        {/* Horizontal video carousel */}
        <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 scrollbar-hide">
          {videos.map((v, idx) => (
            <motion.button
              key={v.id}
              onClick={() => setActiveVideo(v)}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0, margin: '0px 0px 200px 0px' }}
              transition={{ duration: 0.4, delay: idx * 0.05 }}
              className="group snap-center flex-shrink-0 w-[280px] md:w-[360px] relative rounded-2xl overflow-hidden bg-gray-900 cursor-pointer text-left"
              data-testid={`video-thumb-${v.id}`}
            >
              <div className="relative aspect-video">
                <img
                  src={v.thumb}
                  alt={v.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-14 h-14 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center group-hover:scale-110 group-hover:bg-white/25 transition-all duration-300">
                    <Play size={22} className="text-white ml-1" fill="white" />
                  </div>
                </div>
                <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-black/60 backdrop-blur-md text-[10px] font-bold text-white">
                  {v.duration}
                </div>
              </div>
              <div className="p-3 text-white absolute bottom-0 left-0 right-0">
                <h3 className="font-semibold text-sm leading-tight mb-0.5">{v.title}</h3>
                <p className="text-[11px] text-white/75">{v.sub}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Modal player */}
      {activeVideo && (
        <div
          className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4"
          onClick={() => setActiveVideo(null)}
        >
          <button
            className="absolute top-4 right-4 w-11 h-11 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors z-10"
            onClick={() => setActiveVideo(null)}
            aria-label="Close"
          >
            <X size={22} />
          </button>
          <div className="w-full max-w-4xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <iframe
              src={`${activeVideo.videoUrl}?autoplay=1&rel=0`}
              title={activeVideo.title}
              className="w-full h-full rounded-xl"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
            <div className="text-center mt-3">
              <h3 className="text-white text-base font-semibold">{activeVideo.title}</h3>
              <p className="text-xs text-white/70 mt-1">{activeVideo.sub}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default VideoReel;
